import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { sortBy } from 'lodash';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  ColorsV2,
  Field,
  FieldLabel,
  Flex,
  Select,
  Spinner,
  Text,
  getToken,
  spaces,
} from '@electricjs/arc';

import { AIToolChip } from '@/components/AIChat/AIToolChip';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useTicketFlow } from '@/components/TicketFlowContext';
import { useGetLoggedUser } from '@/hooks/useGetLoggedUser';
import {
  COPILOT_STATUS,
  CopilotStatus,
  TOOL_NAMES,
} from '@/helpers/aiChat/tools';
import {
  useGetOrganizationApplicationsQuery,
  useGetRequestTypeCategoriesQuery,
} from '@/redux/slices/organizationApiSlice';
import {
  useCreateRequestMutation,
  useGetRequestTypesByRequestTypeCategoryIdQuery,
  useLazyGetRequestTypeSettingsByRequestTypeIdQuery,
} from '@/redux/slices/requestApiSlice';
import {
  RequestCreationArgs,
  RequestStatus,
  RequestType,
} from '@/types/requests';
import { ToolDisplayStatus } from '@/pages/AIChatCopilotKit/types';
import RequestSupportFormInput from '@/pages/SupportCenter/components/RequestSupportForm/RequestSupportFormInput';
import {
  getBackendTranslationKey,
  getCategoryTranslationKey,
} from '@/pages/SupportCenter/utils/backendTranslations';

const DisclaimerBanner = styled(Flex)`
  background-color: ${getToken(ColorsV2.INFO_LIGHTEST)};
  border: 1px solid ${getToken(ColorsV2.INFO_LIGHT)};
  border-radius: 0.5rem;
  padding: ${spaces.space_s_rem};
  gap: ${spaces.space_xs_rem};
  align-items: flex-start;
`;

const RendererPhase = {
  PENDING: 'pending',
  DISCOVERY: 'discovery',
  FIELD_DEFINITIONS: 'field-definitions',
  FORM_RENDER: 'form-render',
} as const;
type RendererPhase = (typeof RendererPhase)[keyof typeof RendererPhase];

const FieldType = {
  TEXTAREA: 'textarea',
  RADIO: 'radio',
  DROPDOWN: 'dropdown',
  EMPLOYEE_DROPDOWN: 'employeeDropdown',
  APPLICATION_DROPDOWN: 'applicationDropdown',
  CHECKBOX: 'checkbox',
} as const;

function detectPhase(args: Record<string, unknown>): RendererPhase {
  const hasCategory =
    typeof args.category === 'string' && args.category.length > 0;
  const hasTypeId =
    typeof args.request_type_id === 'string' &&
    (args.request_type_id as string).length > 0;
  const hasFieldValues =
    args.field_values !== null &&
    args.field_values !== undefined &&
    typeof args.field_values === 'object' &&
    !Array.isArray(args.field_values);
  if (!hasCategory) {
    return RendererPhase.PENDING;
  }
  if (!hasTypeId) {
    return RendererPhase.DISCOVERY;
  }
  if (!hasFieldValues) {
    return RendererPhase.FIELD_DEFINITIONS;
  }
  return RendererPhase.FORM_RENDER;
}

function safeParseOptions(options: string | null | undefined): unknown {
  if (!options) {
    return null;
  }
  try {
    return JSON.parse(options);
  } catch {
    return null;
  }
}

type ApplicationOption = { name: string; id: string; icon?: string };

function computeDefaultValue(
  field: {
    name: string;
    type: string;
    options?: string | null;
    defaultValue?: unknown;
  },
  fieldValues: Record<string, unknown> | null,
  loggedEmployee: {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  },
  applicationOptions: ApplicationOption[],
  notListedLabel?: string
): unknown {
  if (!fieldValues) {
    return field.defaultValue;
  }
  const raw = fieldValues[field.name];
  if (raw === undefined) {
    return field.defaultValue;
  }

  let result: unknown;

  switch (field.type) {
    case FieldType.TEXTAREA:
    case FieldType.RADIO:
      result = raw;
      break;
    case FieldType.DROPDOWN: {
      const options = safeParseOptions(field.options);
      if (Array.isArray(options)) {
        const match = (
          options as Array<{ value?: unknown; label?: unknown }>
        ).find(o => o.value === raw || o.label === raw);
        result = match ?? field.defaultValue;
      } else {
        result = field.defaultValue;
      }
      break;
    }
    case FieldType.EMPLOYEE_DROPDOWN:
      if (typeof raw === 'string' && raw === loggedEmployee.id) {
        result = loggedEmployee;
      } else if (typeof raw === 'object' && raw !== null) {
        // Normalize backend snake_case (employee_id/first_name/last_name) to
        // frontend camelCase (id/firstName/lastName) so getOptionLabel renders correctly.
        const obj = raw as Record<string, unknown>;
        result = {
          id: obj.id ?? obj.employee_id,
          firstName: obj.firstName ?? obj.first_name,
          preferredFirstName:
            obj.preferredFirstName ?? obj.preferred_first_name,
          lastName: obj.lastName ?? obj.last_name,
          email: obj.email,
        };
      } else {
        result = field.defaultValue;
      }
      break;
    case FieldType.APPLICATION_DROPDOWN:
      if (typeof raw === 'string') {
        result =
          applicationOptions.find(
            o => o.name.toLowerCase() === raw.toLowerCase()
          ) ??
          (notListedLabel
            ? { id: '', name: notListedLabel }
            : field.defaultValue);
      } else {
        result = field.defaultValue;
      }
      break;
    case FieldType.CHECKBOX:
    default:
      result = field.defaultValue;
  }

  return result;
}

type SupportTicketFormRendererProps = {
  args: Record<string, unknown>;
  status: CopilotStatus;
  respond?: (result: string) => void;
  organizationId: string;
  userId: string;
  employeeId: string;
};

export const SupportTicketFormRenderer = ({
  args,
  status,
  respond,
  organizationId,
  userId,
  employeeId,
}: SupportTicketFormRendererProps) => {
  const { t } = useTranslation('support-center');
  const navigate = useNavigate();
  const { showToast } = useGlobalUI();
  const { setTicketCreated } = useTicketFlow();
  const loggedUser = useGetLoggedUser();

  const phase = detectPhase(args);
  const category =
    typeof args.category === 'string' ? args.category : 'Something Else';
  const requestTypeId =
    typeof args.request_type_id === 'string' ? args.request_type_id : null;
  const fieldValues =
    phase === RendererPhase.FORM_RENDER &&
    args.field_values !== null &&
    args.field_values !== undefined
      ? (args.field_values as Record<string, unknown>)
      : null;

  const loggedEmployee = useMemo(
    () =>
      loggedUser
        ? {
            id: loggedUser.employeeId,
            firstName: loggedUser.employeeFirstName,
            lastName: loggedUser.employeeLastName,
            email: loggedUser.email,
          }
        : { id: employeeId },
    [loggedUser, employeeId]
  );

  const [wasRejected, setWasRejected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkboxValues, setCheckboxValues] = useState<
    Record<string, string[]>
  >({});

  // Respond-once and initialize-once guards — both reset when a new tool invocation starts
  const hasRespondedRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const phaseKey = `${args.category ?? ''}|${args.request_type_id ?? ''}|${fieldValues !== null ? 'fv' : ''}`;
  // Tracks the last issue id that was fully initialized, so we can detect user-driven
  // changes to the issue Select after init and reset the form for the new request type.
  const initializedIssueIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    hasRespondedRef.current = false;
    hasInitializedRef.current = false;
  }, [phaseKey]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
    formState: { errors, isValid },
  } = useForm<{ issue: RequestType; [key: string]: unknown }>({
    mode: 'onChange',
  });
  const issue = watch('issue');

  const { data: requestTypeCategories } = useGetRequestTypeCategoriesQuery({
    organizationId,
  });

  const categoryData = useMemo(
    () => requestTypeCategories?.find(c => c.name === category),
    [requestTypeCategories, category]
  );
  const categoryId = categoryData?.id ?? '';

  const { data: requestTypes, isFetching: isFetchingRequestTypes } =
    useGetRequestTypesByRequestTypeCategoryIdQuery(categoryId, {
      skip: !categoryId,
    });

  const [
    getFieldSettings,
    { data: requestTypeSettings, isFetching: isFetchingFieldSettings },
  ] = useLazyGetRequestTypeSettingsByRequestTypeIdQuery();

  const [createRequest] = useCreateRequestMutation();

  const {
    data: orgAppsResponse,
    isSuccess: appsQuerySucceeded,
    isError: appsQueryFailed,
  } = useGetOrganizationApplicationsQuery(
    { organizationId },
    { skip: phase !== RendererPhase.FORM_RENDER || !organizationId }
  );

  const applicationOptions: ApplicationOption[] = useMemo(
    () =>
      (orgAppsResponse?.applications ?? []).map(a => ({
        name: a.application.name,
        id: a.application.id,
        icon: a.application.icon,
      })),
    [orgAppsResponse]
  );

  const sortedFields = useMemo(
    () => sortBy(requestTypeSettings, 'orderValue'),
    [requestTypeSettings]
  );

  // ── Phase 1: auto-respond with request types ──────────────────────────────

  useEffect(() => {
    if (phase !== RendererPhase.DISCOVERY) return;
    if (status !== COPILOT_STATUS.EXECUTING || !respond) return;
    if (hasRespondedRef.current) return;
    if (!categoryData || isFetchingRequestTypes || !requestTypes) return;

    hasRespondedRef.current = true;
    respond(
      JSON.stringify({
        requestTypes: requestTypes.map(rt => ({
          id: rt.id,
          name: rt.name,
          slug: rt.slug,
        })),
        loggedEmployee: {
          employeeId: loggedEmployee.id,
          name: `${loggedEmployee.firstName ?? ''} ${loggedEmployee.lastName ?? ''}`.trim(),
          email: loggedEmployee.email ?? '',
        },
      })
    );
  }, [
    phase,
    status,
    respond,
    categoryData,
    isFetchingRequestTypes,
    requestTypes,
    loggedEmployee,
  ]);

  // ── Phase 2: fetch field settings then auto-respond ───────────────────────

  useEffect(() => {
    if (phase !== RendererPhase.FIELD_DEFINITIONS) return;
    if (status !== COPILOT_STATUS.EXECUTING || !respond) return;
    if (hasRespondedRef.current) return;
    if (isFetchingFieldSettings || requestTypeSettings === undefined) return;

    hasRespondedRef.current = true;
    respond(
      JSON.stringify({
        fields: requestTypeSettings.map(f => ({
          name: f.name,
          displayName: f.displayName,
          type: f.type,
          required: f.required,
          prompt: f.prompt ?? null,
          options: safeParseOptions(f.options),
          parentRequestTypeSettingId: f.parentRequestTypeSettingId ?? null,
          parentSettingValue: f.parentSettingValue ?? null,
        })),
        loggedEmployee: {
          employeeId: loggedEmployee.id,
          name: `${loggedEmployee.firstName ?? ''} ${loggedEmployee.lastName ?? ''}`.trim(),
          email: loggedEmployee.email ?? '',
        },
      })
    );
  }, [
    phase,
    status,
    respond,
    requestTypeSettings,
    isFetchingFieldSettings,
    loggedEmployee,
  ]);

  // ── Phase 3: trigger field settings fetch and pre-fill form ──────────────

  // Single effect handles both phase 2 (field-definitions) and phase 3 (form-render)
  // to avoid duplicate getFieldSettings calls when transitioning between phases.
  useEffect(() => {
    if (!requestTypeId) return;
    if (
      phase === RendererPhase.FIELD_DEFINITIONS ||
      phase === RendererPhase.FORM_RENDER
    ) {
      getFieldSettings(requestTypeId);
    }
  }, [phase, requestTypeId, getFieldSettings]);

  // Set issue type — only run in form-render to avoid setting a wrong value in earlier phases
  useEffect(() => {
    if (phase !== RendererPhase.FORM_RENDER) return;
    if (!requestTypes?.length || issue) return;
    const match = requestTypes.find(rt => rt.id === requestTypeId);
    setValue('issue', match ?? requestTypes[0]);
  }, [phase, requestTypes, requestTypeId, issue, setValue]);

  // Trigger field settings fetch when issue type changes (user changes select)
  useEffect(() => {
    if (issue?.id) {
      getFieldSettings(issue.id);
    }
  }, [issue, getFieldSettings]);

  // When the user changes the issue Select after init, reset the form for the new type.
  // AI field_values belonged to the original request type and are irrelevant here.
  useEffect(() => {
    if (!issue?.id) return;
    if (!hasInitializedRef.current) return;
    if (initializedIssueIdRef.current === issue.id) return;
    initializedIssueIdRef.current = issue.id;
    reset({ issue });
  }, [issue, reset]);

  // Initialize checkbox values from field_values once settings are loaded
  useEffect(() => {
    if (
      phase !== RendererPhase.FORM_RENDER ||
      !fieldValues ||
      !sortedFields.length
    )
      return;
    const updates: Record<string, string[]> = {};
    sortedFields
      .filter(f => f.type === FieldType.CHECKBOX)
      .forEach(f => {
        const raw = fieldValues[f.name];
        if (Array.isArray(raw)) {
          updates[f.name] = raw as string[];
        }
      });
    if (Object.keys(updates).length > 0) {
      setCheckboxValues(prev => ({ ...prev, ...updates }));
    }
  }, [phase, fieldValues, sortedFields]);

  useEffect(() => {
    Object.entries(checkboxValues).forEach(([name, values]) => {
      setValue(name, values);
    });
  }, [checkboxValues, setValue]);

  // Fallback: set applicationDropdown value once applicationOptions loads (async timing fix).
  // Falls back to the "Application not listed" sentinel when the app isn't in the org's integrations.
  // Guard: skip once the form has been initialized (reset() includes the resolved value), so
  // subsequent applicationOptions reference changes don't overwrite the user's selection.
  useEffect(() => {
    if (
      phase !== RendererPhase.FORM_RENDER ||
      !fieldValues ||
      !applicationOptions.length ||
      !sortedFields.length
    )
      return;
    if (hasInitializedRef.current) return;
    const appField = sortedFields.find(
      f => f.type === FieldType.APPLICATION_DROPDOWN
    );
    if (!appField) return;
    const raw = fieldValues[appField.name];
    if (typeof raw !== 'string') return;
    const match = applicationOptions.find(
      o => o.name.toLowerCase() === raw.toLowerCase()
    ) ?? { id: '', name: t('applicationSelect.applicationNotListed') };
    setValue(appField.name, match);
  }, [phase, fieldValues, applicationOptions, sortedFields, setValue, t]);

  // Atomically initialize all form fields from fieldValues once data is ready.
  //
  // Without this, two bugs occur in the pre-filled AI form:
  // 1. Conditional fields don't show: RHF registers Controller defaultValues in effects
  //    (after render), so watch(parentField) returns undefined on the first render and
  //    child fields are hidden. reset() sets all values before the next render.
  // 2. Submit button is permanently disabled: reset() alone doesn't run validation in
  //    onChange mode, so isValid stays false. shouldValidate: true forces a validation
  //    pass immediately after reset, enabling the submit button.
  //
  // applicationDropdown must be included in reset() — if omitted, reset() wipes any value
  // the fallback effect already set (reset clears unspecified fields to undefined). To include
  // it with the correct value, we must wait for applicationOptions to finish loading first.
  useEffect(() => {
    if (
      phase !== RendererPhase.FORM_RENDER ||
      !sortedFields.length ||
      !fieldValues ||
      !requestTypes
    )
      return;
    // Wait for streaming to finish: field_values arrives as {} during streaming and fills
    // in progressively. Firing reset() before streaming completes locks in empty values.
    // status === 'executing' is the signal that the tool call (and its args) are finalized.
    if (status !== COPILOT_STATUS.EXECUTING) return;
    // If the form has an applicationDropdown, wait for applicationOptions so the value
    // can be correctly resolved before reset() locks everything in.
    const hasAppDropdown = sortedFields.some(
      f => f.type === FieldType.APPLICATION_DROPDOWN
    );
    if (hasAppDropdown && !appsQuerySucceeded && !appsQueryFailed) return;
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const issueMatch =
      requestTypes.find(rt => rt.id === requestTypeId) ?? requestTypes[0];
    initializedIssueIdRef.current = issueMatch?.id;
    const defaults: Record<string, unknown> = { issue: issueMatch };
    const notListedLabel = t('applicationSelect.applicationNotListed');
    sortedFields.forEach(field => {
      if (field.type === FieldType.CHECKBOX) {
        const raw = fieldValues[field.name];
        defaults[field.name] = Array.isArray(raw)
          ? raw
          : (field.defaultValue ?? []);
        return;
      }
      defaults[field.name] = computeDefaultValue(
        field,
        fieldValues,
        loggedEmployee,
        applicationOptions,
        notListedLabel
      );
    });

    reset(defaults);
    // onChange mode doesn't validate on reset — trigger forces a validation pass
    // so isValid reflects the pre-filled values immediately.
    trigger();
  }, [
    phase,
    status,
    sortedFields,
    fieldValues,
    requestTypes,
    requestTypeId,
    loggedEmployee,
    applicationOptions,
    appsQuerySucceeded,
    appsQueryFailed,
    reset,
    trigger,
    t,
  ]);

  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleCheckbox = (event: FormEvent, name: string) => {
    if (!event?.target) return;
    const eventTarget = event.target as HTMLInputElement;
    setCheckboxValues(prev => {
      const current = prev[name] ?? [];
      const next = current.includes(eventTarget.value)
        ? current.filter(v => v !== eventTarget.value)
        : [...current, eventTarget.value];
      return { ...prev, [name]: next };
    });
  };

  // Dynamic DB fields prevent full static typing; `issue` is the only known key.
  const onSubmit = useCallback(
    async (data: Record<string, unknown> & { issue: RequestType }) => {
      if (!respond) return;
      setIsSubmitting(true);

      const employeeFieldName = sortedFields.find(
        f => f.type === FieldType.EMPLOYEE_DROPDOWN
      )?.name;
      const payload: RequestCreationArgs = {
        organizationId,
        requestTypeId: data.issue.id,
        requesterUserId: userId,
        status: RequestStatus.InProgress,
        data: data as RequestCreationArgs['data'],
        onBehalfOf:
          (employeeFieldName
            ? (data[employeeFieldName] as { id?: string } | undefined)?.id
            : undefined) ??
          loggedEmployee.id ??
          employeeId,
      };

      try {
        await createRequest(payload).unwrap();
        if (!isMountedRef.current) return;
        showToast({
          id: 'support-ticket-success-toast',
          intent: 'success',
          title: t('requestSupportPage.toasts.success.title'),
          message: t('requestSupportPage.toasts.success.message', {
            email: loggedEmployee.email ?? '',
          }),
        });
        setTicketCreated(true);
        navigate('/support-center');
        respond('Support ticket created successfully');
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          id: 'support-ticket-error-toast',
          intent: 'error',
          title: t('requestSupportPage.toasts.error.title'),
          message: t('requestSupportPage.toasts.error.message'),
        });
        respond(
          `Error: ${error instanceof Error ? error.message : 'Failed to create support ticket'}`
        );
      } finally {
        if (isMountedRef.current) setIsSubmitting(false);
      }
    },
    [
      respond,
      organizationId,
      userId,
      employeeId,
      loggedEmployee.id,
      loggedEmployee.email,
      sortedFields,
      createRequest,
      showToast,
      setTicketCreated,
      navigate,
      t,
    ]
  );

  const handleCancel = useCallback(() => {
    if (!respond) return;
    setWasRejected(true);
    respond('Support ticket creation cancelled by user');
  }, [respond]);

  // ── Chip display status ───────────────────────────────────────────────────

  let displayStatus: ToolDisplayStatus;
  if (
    phase === RendererPhase.PENDING ||
    phase === RendererPhase.DISCOVERY ||
    phase === RendererPhase.FIELD_DEFINITIONS
  ) {
    displayStatus =
      status === COPILOT_STATUS.COMPLETE
        ? ToolDisplayStatus.COMPLETED
        : ToolDisplayStatus.PENDING;
  } else if (status === COPILOT_STATUS.EXECUTING) {
    displayStatus = ToolDisplayStatus.NEEDS_APPROVAL;
  } else if (status === COPILOT_STATUS.COMPLETE && wasRejected) {
    displayStatus = ToolDisplayStatus.REJECTED;
  } else if (status === COPILOT_STATUS.COMPLETE) {
    displayStatus = ToolDisplayStatus.COMPLETED;
  } else {
    displayStatus = ToolDisplayStatus.PENDING;
  }

  const categoryLabel = t(
    `categories.${getCategoryTranslationKey(categoryData?.slug ?? category)}.name`,
    { defaultValue: category }
  );

  // Render nothing for Call 1 (discovery) and Call 2 (field-definitions) —
  // returning an empty wrapper causes the chat UI to render an empty message bubble.
  if (phase !== RendererPhase.FORM_RENDER) return null;

  return (
    <Flex flexDirection="column" style={{ gap: spaces.space_xs_rem }}>
      <AIToolChip
        id="open-support-ticket-tool"
        toolSlug={TOOL_NAMES.OPEN_SUPPORT_TICKET}
        variant={displayStatus}
      />

      {status === COPILOT_STATUS.EXECUTING && respond && (
        <Flex flexDirection="column" style={{ gap: spaces.space_s_rem }}>
          <DisclaimerBanner flexDirection="column">
            <Text variant="subheading">
              {t('requestSupportPage.aiAssistedForm.title')}
            </Text>
            <Text variant="body">
              {t('requestSupportPage.aiAssistedForm.description')}
            </Text>
          </DisclaimerBanner>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex flexDirection="column" style={{ gap: spaces.space_s_rem }}>
              <Text variant="subheading">
                {t('requestSupportPage.newRequestHeading', {
                  category: categoryLabel,
                })}
              </Text>

              {isFetchingRequestTypes ? (
                <Flex justifyContent="center">
                  <Spinner />
                </Flex>
              ) : (
                <>
                  <Field>
                    <FieldLabel id="issue-label">
                      {t('requestSupportPage.fields.issueLabel')}
                    </FieldLabel>
                    <Controller
                      name="issue"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Select
                          id="support-ticket-issue-select"
                          {...field}
                          options={requestTypes}
                          aria-labelledby="issue-label"
                          isLoading={isFetchingRequestTypes}
                          placeholder={t(
                            'requestSupportPage.fields.selectPlaceholder'
                          )}
                          getOptionLabel={(requestType: RequestType) =>
                            t(
                              getBackendTranslationKey(
                                'requestTypes',
                                requestType.slug,
                                'name'
                              ),
                              { defaultValue: requestType.name }
                            )
                          }
                          getOptionValue={(requestType: RequestType) =>
                            requestType.name
                          }
                          isSearchable
                          isClearable
                        />
                      )}
                    />
                    {errors.issue && (
                      <Text variant="body" color={getToken(ColorsV2.ERROR)}>
                        {t('requestSupportPage.fields.fieldRequired')}
                      </Text>
                    )}
                  </Field>

                  {sortedFields.map(item => {
                    let showField = true;
                    if (item.parentRequestTypeSettingId) {
                      const parentSettingName =
                        requestTypeSettings?.find(
                          rts => rts.id === item.parentRequestTypeSettingId
                        )?.name ?? '';
                      const currentParentValue = watch(parentSettingName);
                      // Dropdown fields store the full {value,label} object in RHF;
                      // unwrap to the scalar before comparing against the DB string.
                      const resolvedParentValue =
                        currentParentValue !== null &&
                        typeof currentParentValue === 'object' &&
                        'value' in currentParentValue
                          ? (currentParentValue as { value: unknown }).value
                          : currentParentValue;
                      showField =
                        item.parentSettingValue === resolvedParentValue;
                    }

                    return (
                      showField && (
                        <Field key={item.name}>
                          <FieldLabel id={item.name}>
                            {t(
                              getBackendTranslationKey(
                                'settings',
                                item.name,
                                'displayName'
                              ),
                              { defaultValue: item.displayName }
                            )}
                          </FieldLabel>
                          <Controller
                            name={item.name}
                            control={control}
                            defaultValue={computeDefaultValue(
                              item,
                              fieldValues,
                              loggedEmployee,
                              applicationOptions,
                              t('applicationSelect.applicationNotListed')
                            )}
                            rules={{ required: item.required }}
                            render={({ field }) => (
                              <RequestSupportFormInput
                                {...field}
                                type={item.type}
                                name={item.name}
                                options={
                                  safeParseOptions(item.options) as unknown[]
                                }
                                values={checkboxValues[item.name] ?? []}
                                handleCheckbox={(e: FormEvent) =>
                                  handleCheckbox(e, item.name)
                                }
                              />
                            )}
                          />
                          {errors[item.name] && (
                            <Text
                              variant="body"
                              color={getToken(ColorsV2.ERROR)}>
                              {t('requestSupportPage.fields.fieldRequired')}
                            </Text>
                          )}
                        </Field>
                      )
                    );
                  })}

                  <Box>
                    <Flex style={{ gap: spaces.space_xs_rem }}>
                      <Button
                        id="support-ticket-submit"
                        type="submit"
                        loading={isSubmitting}
                        disabled={!isValid || isSubmitting}>
                        {t('requestSupportPage.submitButton')}
                      </Button>
                      <Button
                        id="support-ticket-cancel"
                        type="button"
                        variant="outline"
                        intent="neutral"
                        onClick={handleCancel}
                        disabled={isSubmitting}>
                        {t('requestSupportPage.cancelButton')}
                      </Button>
                    </Flex>
                  </Box>
                </>
              )}
            </Flex>
          </form>
        </Flex>
      )}
    </Flex>
  );
};
